/* VANIL A BEAN — mobile "Why it tastes different", merged into the tasting-notes
   stage (#x-sensory .xsp-stage).

   WHY THIS FILE STOPPED BEING A SECTION
   -------------------------------------
   It used to be its own pinned section, #xcmm, sitting directly above #x-sensory.
   Both were 100vh stages pinned in flow, and two 100vh stages in flow CANNOT
   overlap: one has to leave before the other arrives. Measured on the live page at
   390x844, that cost exactly one screen:

     #xcmm  pin ends   scrollY 9108
     #xsp   pin starts scrollY 9952      -> 844px, one full viewport, unpinned

   and the compounds copy had already reached opacity 0 at 8958, 150px before its
   own pin even ended. Sampling ink actually drawn on the two canvases through that
   gap gave 0.0% / 0.0% for the first ~280px — a blank cream screen — then ~400px
   of grain under 1% coverage, then the bean's own ink stepping 0.0% -> 1.7% in a
   single 140px move. That is the "dead scroll" and the "jumps into the full bean"
   in one measurement, and no amount of retiming fixes it, because the gap is
   geometry, not choreography.

   So the two scenes now share ONE pin. This file relocates the compounds copy into
   the tasting-notes stage as a layer, collapses #xcmm to zero scroll, and hands the
   opening 26% of the merged stage to that layer (MOBILE.preface in vab-sensory.js).
   The bean builds during the compounds exit, in the same stage, so the handover is
   a cross-dissolve rather than a gap.

   Desktop is untouched: the guard returns above 899px, every rule is inside one
   max-width:899px block, and DESKTOP defines no `preface` so vab-sensory.js skips
   the remap entirely. On desktop #x-compounds and #x-sensory remain two sections.

   SPECIFICITY NOTE — this cost a whole shipped change once. An asset injects its
   <style> into <head>; the product liquid's <style> sits in <body>, later in
   document order. At EQUAL specificity the liquid wins, so asset rules that merely
   matched the liquid's selectors were silently inert on the live theme (the runway
   trims never applied — the section still ran 200svh). Every rule here is prefixed
   `html body` so it outranks the liquid outright and one upload is genuinely
   enough. */
(function () {
  try {
    if (window.innerWidth >= 900) return;

    /* TEXT FIRST, THEN THE SAND (2026-09-24, v3). The comparison table is retired
       on mobile. The chapter is typography only: one compound for synthetic
       vanilla, then the count runs to two hundred for Bourbon vanilla. Nothing is
       drawn until the next scroll, when the bean rises as sand from below the
       screen and forms alongside "One bean." (vab-sensory.js).

       This object is the single source of truth for the chapter's timing. The
       numeral counts 1 -> 200 on `m`, the multiply amount (0-1) for a stage
       progress; it grows exponentially, the way living things multiply. */
    (function () {
      var N = 200, S = 0.08, E = 0.135, BW = 0.12, LN = Math.log(N);
      window.VAB_MULT = {
        N: N, bw: BW,
        /* the rest of the preface, in stage progress (pIn): the sand's rise window
           (vab-sensory.js), when the count leaves, and the DIRECTOR'S MARKS - the
           composed frames of this chapter (one compound / two hundred). */
        T: { rise: [0.145, 0.29], out: [0.15, 0.04], marks: [0.004, 0.143] },
        m: function (p) { var t = (p - S) / (E - S); t = t < 0 ? 0 : t > 1 ? 1 : t; return t * t * (3 - 2 * t); },
        birth: function (k) { return (Math.log(k + 1) / LN) * (1 - BW); },
        count: function (m) { var x = m / (1 - BW); x = x > 1 ? 1 : x; return Math.max(1, Math.min(N, Math.floor(Math.exp(x * LN) + 1e-6))); }
      };
    })();

    var CSS = `
@media(max-width:899px){
  /* #xcmm gives up its scroll entirely — its 200svh is what pays for the preface.
     Not display:none: the element stays in the DOM so anything holding a reference
     to it (an anchor link, a nav observer) still resolves, it simply occupies
     nothing. !important because the liquid also sets a height on it. */
  html body #xcmm{height:0!important;min-height:0!important;margin:0!important;
    padding:0!important;overflow:hidden!important;border:0!important}
  html body #xcmm .xcmm-pin{display:none!important}

  /* The relocated layer. Absolute inside .xsp-stage (which is position:relative and
     the pinned element), above the bean canvas so the text reads over it while the
     bean assembles underneath — that overlap IS the transition.
     Top clearance is the same measured pill offset every other mobile scene uses,
     and the horizontal column is the page's 6.5%, so the copy lands on the same
     left rule as the ingredients and tasting-notes type. */
  html body #x-sensory .xcm-layer{
    position:absolute;left:0;right:0;top:0;
    padding:var(--xcmm-clear,202px) 6.5% 0;
    z-index:7;box-sizing:border-box;
    will-change:opacity,transform}
  html body #x-sensory .xcm-layer.is-gone{pointer-events:none}

  /* .xcm-mob keeps the type it already had; it only loses the block padding it
     used as a standalone stacked section, since the layer now owns the framing. */
  html body #x-sensory .xcm-mob{padding:0!important;background:transparent!important;
    margin:0!important}
  /* ONE LEFT EDGE: the layer already carries the 6.5% gutter, so the caption's own
     24px (from its old life as a stacked section) doubled it - the headline started
     at 49px while every other line on the page starts at 25. */
  html body #x-sensory .xcm-mob .xcm-cap{padding-left:0!important;padding-right:0!important}
  html body #x-sensory .xcm-cap,
  html body #x-sensory .xcm-index{will-change:opacity,transform}

  /* The divider is the hand-off — it collapses last, closing the argument as the
     bean finishes forming beneath it. */
  html body #x-sensory .xcm-divider{
    display:block!important;width:auto;height:1px;margin:30px 0 0;
    background:rgba(32,24,15,.18);transform:scaleX(0);transform-origin:left center;
    will-change:transform}

  /* The table stays in the document for screen readers; it just stops being
     seen. The count below says the same thing in a way you can watch. */
  html body #x-sensory .xcm-layer.is-mult .xcm-index{position:absolute!important;width:1px!important;
    height:1px!important;overflow:hidden!important;clip:rect(0 0 0 0);clip-path:inset(50%);
    white-space:nowrap;margin:0!important;padding:0!important}
  html body #x-sensory .xcm-layer.is-mult .xcm-divider{display:none!important}
  /* THE NUMERAL IS THE HERO (2026-09-26). The count used to sit small at 73%,
     leaving the middle of the frame empty. Now it fills it: one very large figure
     on the left edge, its caption tucked beneath - the whole argument in a glance. */
  html body #x-sensory .xcm-count{position:absolute;left:6.5%;right:6.5%;top:47%;z-index:7;
    pointer-events:none;opacity:0;will-change:opacity,transform}
  html body #x-sensory .xcm-num{font-family:var(--serif);font-weight:400;font-size:clamp(120px,38vw,168px);line-height:.84;
    letter-spacing:-.055em;margin-left:-.04em!important;color:#20180F;font-variant-numeric:tabular-nums lining-nums;margin:0}
  html body #x-sensory .xcm-say{display:grid;margin-top:22px}
  html body #x-sensory .xcm-say > p{grid-area:1/1;margin:0}
  html body #x-sensory .xcm-say-e{display:block;font-family:var(--brandcaps,var(--sans));font-size:11px;
    font-weight:500;letter-spacing:.24em;text-transform:uppercase;color:#8A7860}
  html body #x-sensory .xcm-say-d{display:block;margin-top:8px;font-family:var(--sans);font-size:15px;
    line-height:1.5;color:#6B5C48}

  @media(prefers-reduced-motion:reduce){
    html body #x-sensory .xcm-cap,
    html body #x-sensory .xcm-index,
    html body #x-sensory .xcm-divider{transform:none!important}
  }
}`;
    var st = document.createElement('style');
    st.textContent = CSS;
    document.head.appendChild(st);

    /* ---------------------------------------------------------------- relocate
       The stage may not exist yet depending on script order, so poll briefly
       rather than assuming. Bounded: if it never appears we leave the page exactly
       as it was rather than half-migrated. */
    var tries = 0;
    (function attach() {
      var stage = document.querySelector('#x-sensory .xsp-stage');
      var src = document.querySelector('#xcmm .xcm-mob');
      if (!stage || !src) {
        if (++tries < 90) return requestAnimationFrame(attach);
        return;                                   /* leave #xcmm as it was */
      }

      var layer = document.createElement('div');
      layer.className = 'xcm-layer';
      layer.appendChild(src);                     /* moves, not clones */
      var div = document.getElementById('xcmm-div');
      if (div && !layer.contains(div)) layer.appendChild(div);
      stage.appendChild(layer);
      document.documentElement.classList.add('vab-xcmm-merged');

      /* The count: a numeral and two captions harvested from the table it
         replaces, so the words stay the merchant's and nothing is retyped here. */
      var count = null;
      var its = src.querySelectorAll('.xcm-index-item');
      if (its.length >= 2 && window.VAB_MULT) {
        var txt = function (el, s) { var n = el.querySelector(s); return n ? n.textContent.replace(/\s+/g, ' ').trim() : ''; };
        count = document.createElement('div');
        count.className = 'xcm-count'; count.setAttribute('aria-hidden', 'true');
        var h = '<p class="xcm-num">1</p><div class="xcm-say">';
        for (var k = 0; k < 2; k++) {
          h += '<p class="xcm-say-' + (k ? 'b' : 'a') + '"><span class="xcm-say-e"></span><span class="xcm-say-d"></span></p>';
        }
        count.innerHTML = h + '</div>';
        var ps = count.querySelectorAll('.xcm-say > p');
        for (var k2 = 0; k2 < 2; k2++) {
          ps[k2].querySelector('.xcm-say-e').textContent = txt(its[k2], '.xcm-index-eyebrow');
          ps[k2].querySelector('.xcm-say-d').textContent = txt(its[k2], '.xcm-index-detail');
        }
        stage.appendChild(count);
        layer.classList.add('is-mult');
      }
      layer.__count = count;

      /* #xcmm has just collapsed and #x-sensory has just grown, so every trigger
         below them is measuring against a stale document. Refresh once, after
         layout has settled. */
      requestAnimationFrame(function () {
        try { (window.vjxSafeRefresh || (window.ScrollTrigger && ScrollTrigger.refresh) || function () {})(); } catch (e) {}
      });

      drive(layer);
    })();

    /* --------------------------------------------------------------- behaviour
       Driven off #x-sensory's own geometry rather than off its ScrollTrigger, so
       this file needs no handle on the other file's internals. The arithmetic is
       exactly what ScrollTrigger computes for start:'top top', end:'bottom bottom',
       which is how that trigger is configured — so the two layers cannot disagree
       about where they are in the scene. */
    function drive(layer) {
      var host = document.getElementById('x-sensory'); if (!host) return;
      var cap = layer.querySelector('.xcm-cap');
      var items = [].slice.call(layer.querySelectorAll('.xcm-index-item'));
      var divider = layer.querySelector('#xcmm-div, .xcm-divider');
      var count = layer.__count, num = null, sayA = null, sayB = null, lastN = -1;
      if (count) { num = count.querySelector('.xcm-num'); sayA = count.querySelector('.xcm-say-a'); sayB = count.querySelector('.xcm-say-b'); }
      if (!cap || !items.length) return;

      var cl = function (x) { return x < 0 ? 0 : x > 1 ? 1 : x; };
      function eOut(t) { return 1 - Math.pow(1 - t, 3); }
      function eIn(t) { return t * t * t; }

      /* Must match MOBILE.preface in vab-sensory.js. If they disagree the bean
         scene starts before or after the compounds layer has cleared, which is
         visible immediately — so it is asserted at runtime rather than trusted. */
      var PREFACE = 0.26;
      var EX_S = 0.14, EX_E = PREFACE;   /* the exit, entirely inside the preface */
      /* ENTRANCE, OVERLAPPING THE CHAPTER BEFORE (2026-10-01). It used to begin at
         18% of the stage's approach, i.e. after Read the Label had completely gone,
         which left ~1.5s of empty paper in the glide between the two chapters. The
         approach is now read UNCLAMPED (negative = Read the Label still finishing),
         so "One compound." starts to arrive as "That's the entire recipe." leaves -
         a crossfade in place, no blank frame. -0.18 is Read the Label at p .93. */
      var ARR = [-0.18, 0.40];

      /* Clearance under the floating pill, measured from the live element — it
         grows a second row when its menu opens, so a value read once is stale from
         that moment. Re-read every frame, applied only on change. */
      var lastClr = -1;
      function clearance() {
        var pill = document.getElementById('vab-anav'), c = 0;
        if (pill) { var r = pill.getBoundingClientRect(); if (r.height > 4) c = r.bottom; }
        if (!(c > 0)) c = 176;
        c = Math.round(c + 26);
        if (c === lastClr) return;
        lastClr = c; host.style.setProperty('--xcmm-clear', c + 'px');
      }

      var smooth = 0, gone = false;
      function frame() {
        clearance();
        var r = host.getBoundingClientRect(), vh = window.innerHeight || 1;
        /* LOCK 1 (2026-09-23): the section now opens with the Read the Label chapter.
           Read against the same virtual geometry as vab-sensory.js - shifted by that
           lead - so this layer's timing is exactly what it was. */
        var L = window.VAB_LEAD1 ? window.VAB_LEAD1.px() : 0;
        var rt = r.top + L;
        var pinLen = Math.max(1, host.offsetHeight - vh - L);
        var target = cl(-rt / pinLen);
        /* The same 0.12 constant vab-sensory.js smooths with. Two layers easing at
           two different rates across a cross-dissolve is exactly the kind of small
           mismatch that reads as jank without being nameable. */
        smooth += (target - smooth) * 0.12;
        var p = smooth;

        var aprU = 1 - rt / vh, apr = cl(aprU);
        /* Math.max over approach and pinned ramps, so the lock is a no-op rather
           than a restart — the approach term is already 1 when the pin engages. */
        var inn = Math.max(cl((aprU - ARR[0]) / (ARR[1] - ARR[0])), cl(p / 0.04));
        var ex = cl((p - EX_S) / (EX_E - EX_S));

        for (var i = 0; i < items.length; i++) {
          var e = eIn(cl((ex - i * 0.14) / 0.44));
          items[i].style.opacity = Math.min(inn, 1 - e).toFixed(3);
          items[i].style.transform = 'translateY(' + ((1 - inn) * 22 - e * 28).toFixed(1) + 'px)';
        }
        var eh = eIn(cl((ex - 0.34) / 0.42));
        cap.style.opacity = Math.min(inn, 1 - eh).toFixed(3);
        cap.style.transform = 'translateY(' + ((1 - inn) * 22 - eh * 40).toFixed(1) + 'px)';

        if (divider) {
          var dIn = eOut(Math.max(cl((apr - 0.45) / 0.5), cl((p - 0.01) / 0.05)));
          var dOut = eOut(cl((ex - 0.10) / 0.90));
          divider.style.transform = 'scaleX(' + Math.max(0, dIn - dOut).toFixed(3) + ')';
        }

        if (count) {
          /* arrives just after the headline, with the single grain; leaves as the
             two hundred begin to build the pod (the bean window opens at 0.143) */
          /* THE COUNT RUNS IN TIME (2026-10-01). Tied straight to scroll, the count
             crossed 1 -> 200 in the fraction of a second the director's glide spent
             in its window - so it read as a jump, not a count. The scroll now sets
             where the count is HEADING; the numeral travels there at a fixed pace
             (the full climb in ~1.15s, exponential, so it accelerates the way living
             things multiply). Back up the page and it counts down the same way. */
          var M = window.VAB_MULT, mT = M.m(p), now = performance.now();
          var dt = Math.min(0.05, Math.max(0, (now - (count.__t || now)) / 1000)); count.__t = now;
          if (count.__m == null) count.__m = mT;
          var step = dt / 1.15;
          count.__m += Math.max(-step, Math.min(step, mT - count.__m));
          var mm = count.__m;
          var cIn = Math.max(cl((aprU - 0.05) / 0.45), cl(p / 0.03));
          var cOut = eIn(cl((p - M.T.out[0]) / M.T.out[1]));
          count.style.opacity = Math.min(eOut(cIn), 1 - cOut).toFixed(3);
          count.style.transform = 'translateY(' + ((1 - eOut(cIn)) * 16 - cOut * 24).toFixed(1) + 'px)';
          var n = M.count(mm);
          if (n !== lastN) { lastN = n; num.textContent = String(n); }
          /* sequential, never overlapping: two lines of small type crossfading on
             top of each other read as a smudge */
          var sa = cl((mm - 0.04) / 0.16), sb = cl((mm - 0.24) / 0.18);
          sa = sa * sa * (3 - 2 * sa); sb = sb * sb * (3 - 2 * sb);
          sayA.style.opacity = (1 - sa).toFixed(3); sayB.style.opacity = sb.toFixed(3);
          if (count.__g !== (cOut >= 1)) { count.__g = cOut >= 1; count.style.visibility = count.__g ? 'hidden' : ''; }
        }

        /* Stop intercepting taps once it has gone, and stop the layer sitting over
           the tasting-notes copy in the accessibility tree. */
        var g = ex >= 1;
        if (g !== gone) { gone = g; layer.classList.toggle('is-gone', g); layer.style.visibility = g ? 'hidden' : ''; }

        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }
  } catch (e) {}
})();